/**
 * Payment API service — Razorpay integration.
 */

import apiClient from "./client";

export interface CreateOrderRequest {
  project_id: string;
  amount: number; // in paise (₹1 = 100 paise)
  currency?: string;
  description?: string;
}

export interface CreateOrderResponse {
  order_id: string;
  amount: number;
  currency: string;
  key_id: string;
  payment_id: string | null;
  applied_promo_code?: string | null;
  discount_amount?: number;
  line_items?: { label: string; amount: number }[] | null;
}

export interface VerifyPaymentRequest {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
  payment_id?: string | null;
}

export interface VerifyPaymentResponse {
  verified: boolean;
  payment_id: string;
  status: string;
}

/** 02 Nov payment-failure law: openRazorpayCheckout settles via /payments/verify
 * internally, but hands call sites the RAW Razorpay handler response PLUS the
 * verify result — coin lanes re-verify (credits/buy/verify), download lanes
 * trust the settle. Everyone sees both. */
export interface VerifiedPaymentPayload extends VerifyPaymentResponse {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}

export interface PaymentOut {
  id: string;
  project_id: string;
  customer_id: string;
  designer_id: string;
  amount: string;
  payment_type: string; // "escrow" | "download" | ...
  model_ref: string | null; // downloads: provider:task_id
  commission_rate: string;
  commission_amount: string;
  designer_payout_amount: string;
  status: string;
  razorpay_order_id: string | null;
  razorpay_payment_id: string | null;
  paid_at: string | null;
  created_at: string;
}

export async function createOrder(req: CreateOrderRequest): Promise<CreateOrderResponse> {
  const res = await apiClient.post<CreateOrderResponse>("/payments/create-order", req);
  return res.data;
}

/** Skill-hourly design escrow (09-16): amount computed server-side from the
 *  assigned designer's tier rate × Work Watch billable hours. */
export async function createDesignOrder(
  req: { project_id: string; currency?: string },
): Promise<CreateOrderResponse> {
  const res = await apiClient.post<CreateOrderResponse>("/payments/create-design-order", req);
  return res.data;
}

export async function verifyPayment(req: VerifyPaymentRequest): Promise<VerifyPaymentResponse> {
  const res = await apiClient.post<VerifyPaymentResponse>("/payments/verify", req);
  return res.data;
}

export async function listPayments(skip = 0, limit = 20): Promise<PaymentOut[]> {
  const res = await apiClient.get<PaymentOut[]>(`/payments?skip=${skip}&limit=${limit}`);
  return res.data;
}

export async function getPayment(paymentId: string): Promise<PaymentOut> {
  const res = await apiClient.get<PaymentOut>(`/payments/${paymentId}`);
  return res.data;
}

export async function processPayout(paymentId: string): Promise<PaymentOut> {
  const res = await apiClient.post<PaymentOut>(`/payments/${paymentId}/payout`);
  return res.data;
}

// ── 3D Model Download Payments ───────────────────────────────────────────────

// Download price law (17 Oct): matches backend download_price_for_model_ref —
// A0 (openscad) parametric STL ₹50 for signed-in users; registry-engine
// (AI engines) models have the DOWNLOAD INCLUDED in the generation price
// (the download order path is blocked at the backend — 409/402 — because the
// paid generation already grants access); other providers ₹100 legacy.
// GUEST paid downloads (DownloadGateModal path) price ₹100 per backend law.
export const DOWNLOAD_PRICE_A0_PAISE = 5000;
export const DOWNLOAD_PRICE_PAID_PAISE = 10000;

// AI engine ids (mirror backend/app/services/fal_engines.py)
const FAL_LINEUP_IDS = [
  "trellis", "sam3d-objects", "sam3d-body",
  "tripo3d-text", "tripo3d-image", "tripo3d-multiview",
  "trellis-2", "hunyuan3d-v3-image", "hunyuan3d-v3-text", "rodin-v25",
];

// Registry engines: the generation price (in coins) IS the download-inclusive price.
const FAL_LINEUP_PRICE_COINS: Record<string, number> = {
  trellis: 50, "sam3d-objects": 120, "sam3d-body": 120,
  "tripo3d-text": 140, "tripo3d-image": 150, "tripo3d-multiview": 150,
  "trellis-2": 150, "hunyuan3d-v3-image": 160, "hunyuan3d-v3-text": 160,
  "rodin-v25": 160,
};

export function isFalLineupModel(modelRef: string): boolean {
  const id = modelRef.toLowerCase().split(":")[0];
  return FAL_LINEUP_IDS.includes(id);
}

export function downloadPricePaise(modelRef: string): number {
  const ref = modelRef.toLowerCase();
  if (ref.startsWith("openscad:")) return DOWNLOAD_PRICE_A0_PAISE;
  const engineId = ref.split(":")[0];
  if (FAL_LINEUP_PRICE_COINS[engineId]) return FAL_LINEUP_PRICE_COINS[engineId] * 100;
  return DOWNLOAD_PRICE_PAID_PAISE;
}

// rupees for display from a model ref ("openscad:abc" → 50)
export function downloadPriceRupees(modelRef: string): number {
  return downloadPricePaise(modelRef) / 100;
}

export interface CreateDownloadOrderRequest {
  model_ref: string;
  amount?: number;
  currency?: string;
  description?: string;
  promo_code?: string;
}

export async function createDownloadOrder(req: CreateDownloadOrderRequest): Promise<CreateOrderResponse> {
  const res = await apiClient.post<CreateOrderResponse>("/payments/create-download-order", req);
  return res.data;
}

// ── Customer Marketplace (customer-as-seller, 09-21) ──────────────────────────

export interface SellIntentRequest {
  commission_ask_pct: number; // 1–14 (server-enforced; default 5)
  seller_note?: string | null;
}

export interface CustomerListing {
  id: string;
  project_id: string;
  project_number?: string | null;
  title?: string | null;
  seller_name?: string | null;
  commission_ask_pct: number;
  status: "proposed" | "approved" | "listed" | "paused" | "declined";
  list_price?: number | null;
  stl_price?: number | null;
  base_quote_amount?: number | null;
  seller_note?: string | null;
  decline_reason?: string | null;
  created_at?: string | null;
  sold_payment_id?: string | null;
  is_public?: boolean;
  existing?: boolean;
}

export async function createSellIntent(projectRef: string, req: SellIntentRequest): Promise<{ listing: CustomerListing; existing: boolean }> {
  const res = await apiClient.post(`/projects/${projectRef}/sell-intent`, req);
  return res.data;
}

export async function getSellIntent(projectRef: string): Promise<{ listing: CustomerListing | null }> {
  const res = await apiClient.get(`/projects/${projectRef}/sell-intent`);
  return res.data;
}

export async function myListings(): Promise<{ listings: CustomerListing[]; earnings_total: number }> {
  const res = await apiClient.get("/my/listings");
  return res.data;
}

export async function adminListListings(statusFilter?: string): Promise<CustomerListing[]> {
  const res = await apiClient.get("/admin/customer-listings", { params: statusFilter ? { status_filter: statusFilter } : {} });
  return res.data;
}

export interface ListingPatch {
  status: "approved" | "declined" | "paused" | "listed";
  list_price?: number | null; // ₹ override; omitted → formula
  stl_price?: number | null;  // ₹ override; omitted → formula
  decline_reason?: string | null;
}

export async function adminPatchListing(listingId: string, patch: ListingPatch): Promise<CustomerListing> {
  const res = await apiClient.patch(`/admin/customer-listings/${listingId}`, patch);
  return res.data;
}

/** Buy the STL of a customer listing (SKU B — stl_price = quote × 1.35). */
export async function createListingStlOrder(listingId: string): Promise<CreateOrderResponse> {
  const res = await apiClient.post<CreateOrderResponse>("/payments/create-listing-stl-order", { listing_id: listingId });
  return res.data;
}

export function listingStlDownloadUrl(listingId: string): string {
  return `/api/listings/${listingId}/download-stl`;
}

export interface DownloadAccessResponse {
  has_access: boolean;
  payment_id: string | null;
  model_ref: string | null;
}

export async function checkDownloadAccess(modelRef: string): Promise<DownloadAccessResponse> {
  const res = await apiClient.get<DownloadAccessResponse>(`/payments/download-access/${modelRef}`);
  return res.data;
}

// ── 3D Model Download Links (payment-gated) ───────────────────────────────────

export interface DownloadLinksResponse {
  model_ref: string;
  model_mesh: Record<string, string> | null;
  model_urls: Record<string, string> | null;
  texture_urls: Record<string, string>[] | null;
  rendered_image: Record<string, string> | null;
  message: string | null;
  // RE-DOWNLOAD LAW (21 Oct): free re-downloads left for this model/owner
  redownloads_left?: number | null;
}

export async function getDownloadLinks(provider: string, taskId: string): Promise<DownloadLinksResponse> {
  const res = await apiClient.get<DownloadLinksResponse>(`/3d/download/${provider}/${taskId}`);
  return res.data;
}

// ── RE-DOWNLOAD LAW (21 Oct founder): 2 free re-downloads, then ₹20 ──────────
/** ₹20 re-download order (user OR guest — the JWT decides ownership). */
export async function createRedownloadOrder(modelRef: string): Promise<CreateOrderResponse> {
  const res = await apiClient.post<CreateOrderResponse>("/payments/create-redownload-order", {
    model_ref: modelRef,
    currency: "INR",
  });
  return res.data;
}

// ── Dual payment paths (17 Oct founder law: coins OR Razorpay — direct) ──────

/** GUEST direct download purchase — no login: the silent guest JWT carries ownership. */
export async function createGuestDownloadOrder(modelRef: string): Promise<CreateOrderResponse> {
  const res = await apiClient.post<CreateOrderResponse>("/payments/guest/create-download-order", {
    model_ref: modelRef,
  });
  return res.data;
}

/** GUEST verify — settles the guest-scoped payment, no login needed. */
export async function verifyGuestDownload(
  razorpay_order_id: string,
  razorpay_payment_id: string,
  razorpay_signature: string,
): Promise<VerifyPaymentResponse> {
  const res = await apiClient.post<VerifyPaymentResponse>("/payments/guest/verify-download", {
    razorpay_order_id, razorpay_payment_id, razorpay_signature,
  });
  return res.data;
}

/** SIGNED-IN coin payment for a download (A0 ₹50 / legacy-paid models). */
export async function payDownloadWithCoins(modelRef: string): Promise<{ paid: boolean; coins_spent: number; balance_after: number }> {
  const res = await apiClient.post("/payments/download/pay-with-coins", { model_ref: modelRef });
  return res.data;
}

// ── Razorpay Checkout Helper ─────────────────────────────────────────────────

declare global {
  interface Window {
    Razorpay?: new (options: RazorpayOptions) => { open: () => void };
  }
}

export interface RazorpayOptions {
  key: string;
  amount: number; // paise
  currency: string;
  order_id: string;
  name: string;
  description?: string;
  image?: string;
  handler: (response: {
    razorpay_payment_id: string;
    razorpay_order_id: string;
    razorpay_signature: string;
  }) => void;
  prefill?: {
    name?: string;
    email?: string;
    contact?: string;
  };
  theme?: { color: string };
  modal?: { ondismiss: () => void };
}

let razorpayScriptLoaded = false;

function loadRazorpayScript(): Promise<void> {
  if (razorpayScriptLoaded) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => {
      razorpayScriptLoaded = true;
      resolve();
    };
    script.onerror = () => reject(new Error("Failed to load Razorpay checkout script"));
    document.body.appendChild(script);
  });
}

export async function openRazorpayCheckout(opts: {
  orderId: string;
  amount: number; // paise
  currency: string;
  keyId: string;
  description: string;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  onSuccess: (response: VerifiedPaymentPayload) => void;
  onFailure: (error: string) => void;
  /** guest mode → verify via the guest-scoped endpoint (no login) */
  guestMode?: boolean;
}): Promise<void> {
  await loadRazorpayScript();

  if (!window.Razorpay) {
    opts.onFailure("Razorpay checkout not available");
    return;
  }

  const rzp = new window.Razorpay({
    key: opts.keyId || import.meta.env.VITE_RAZORPAY_KEY_ID || "",
    amount: opts.amount,
    currency: opts.currency,
    order_id: opts.orderId,
    name: "FOFUS Design Platform",
    description: opts.description,
    image: "https://fofus.in/favicon.ico",
    prefill: {
      name: opts.customerName,
      email: opts.customerEmail,
      contact: opts.customerPhone,
    },
    theme: { color: "#D4A017" },
    handler: async (response) => {
      try {
        // 17 Oct dual-path law: guests verify through their own session-scoped
        // endpoint (no login); signed-in users through the standard one.
        const result = opts.guestMode
          ? await verifyGuestDownload(
              response.razorpay_order_id,
              response.razorpay_payment_id,
              response.razorpay_signature,
            )
          : await verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
        // 02 Nov payment-failure law: NEVER hand the verify result alone.
        // Spread raw Razorpay ids + verify result — coin call sites read the
        // ids; download call sites read verified/payment_id. Both always there.
        opts.onSuccess({ ...response, ...result });
      } catch (err) {
        opts.onFailure(err instanceof Error ? err.message : "Payment verification failed");
      }
    },
    modal: {
      ondismiss: () => {
        opts.onFailure("Payment cancelled");
      },
    },
  });

  rzp.open();
}